import styled, {css} from 'styled-components'
import {Checkbox as AriaCheckbox} from 'react-aria-components'
import {
    extraSmallFont,
    smallFont,
    mediumSmallFont,
    mediumFont,
    mediumLargeFont,
    largeFont,
    extraLargeFont,
} from '../../../theme/font-styles'

export interface StyledCheckboxProps {
    $fontSize?: 'xs' | 'sm' | 'md' | 'md-lg' | 'lg' | 'xl' | '2xl'
    $checkboxSize?: 'sm' | 'md' | 'lg'
}

const fontSizeMap = {
    xs: extraSmallFont,
    sm: smallFont,
    md: mediumSmallFont,
    'md-lg': mediumFont,
    lg: mediumLargeFont,
    xl: largeFont,
    '2xl': extraLargeFont,
}

const checkboxSizeMap = {
    sm: {
        box: '1rem',
        svg: '0.85rem',
    },
    md: {
        box: '1.143rem',
        svg: '1rem',
    },
    lg: {
        box: '1.285rem',
        svg: '1.143rem',
    },
}

export const Root = styled(AriaCheckbox)<StyledCheckboxProps>`
    --selected-color: #1f88e5;
    --selected-color-pressed: var(--highlight-background-pressed);
    --checkmark-color: white;
    --border-color: #1f88e5;

    display: flex;
    align-items: center;
    gap: 0.571rem;
    color: var(--text-color);
    forced-color-adjust: none;

    ${({$fontSize = 'md'}) => fontSizeMap[$fontSize]()}

    ${({$checkboxSize = 'md'}) => {
        const {box, svg} = checkboxSizeMap[$checkboxSize]
        return css`
            .checkbox {
                width: ${box};
                height: ${box};
                border: 2px solid var(--border-color);
                border-radius: 4px;
                transition: all 200ms;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .checkbox svg {
                width: ${svg};
                height: ${svg};
                fill: none;
                stroke: var(--checkmark-color);
                stroke-width: 3px;
                stroke-dasharray: 22px;
                stroke-dashoffset: 66;
                transition: all 200ms;
            }
        `
    }}

    &[data-pressed] .checkbox {
        border-color: var(--border-color-pressed);
    }

    &[data-focus-visible] .checkbox {
        outline: 2px solid var(--focus-ring-color);
        outline-offset: 2px;
    }

    &[data-selected],
    &[data-indeterminate] {
        .checkbox {
            border-color: var(--selected-color);
            background: var(--selected-color);
        }

        &[data-pressed] .checkbox {
            border-color: var(--selected-color-pressed);
            background: var(--selected-color-pressed);
        }

        .checkbox svg {
            stroke-dashoffset: 44;
        }
    }

    &[data-indeterminate] {
        .checkbox svg {
            stroke: none;
            fill: var(--checkmark-color);
        }
    }

    &[data-disabled] {
        opacity: 0.6;
        cursor: not-allowed;

        .checkbox {
            background: #f5f5f5;
            border-color: #ccc;
        }

        .checkbox svg {
            stroke: #999;
        }
    }
`
